// Built-in signals - the curated detection set the dashboard surfaces.
//
// Detection runs in ONE place: the backend signals-catalog
// (backend/signals-catalog/<name>/signal.py, run by the skwach zygote - see
// docs/specs/signals-contract.md). Each entry here is the dashboard's IDENTITY
// for one of those signals - id, command slug, display title and description,
// plus the backend namespace it reads verdicts from (SIGNAL_BACKEND below). It
// carries NO detector source: the dashboard reads verdicts from `signal_tags`,
// resolves span highlights from the persisted evidence envelope each verdict
// carries (persistedSignalEvidence.js), and shows a signal's definition by
// fetching the deployed source from the backend (useSignalSource.js). Nothing
// reruns a detector in the browser, so nothing here can drift from what fired.
//
// The one kernel still defined in this file is the client-only token-efficiency
// signal at the bottom, which has no backend producer yet.

// A catalog detection's dashboard identity. `family` (the backend signal_tags
// namespace this signal reads from) and `tier` (console alert vs audit) are
// assigned from SIGNAL_BACKEND below - see that map for the authoritative
// command->backend wiring. `steps` is empty on purpose: there is no client-side
// source to run (isCheapTrigger is false, and SignalSourceEditor renders the
// backend source fetched at view time).
const signalDef = (id, command, title, description) => ({
    id,
    command,
    title,
    description,
    family: null,
    tier: "alert",
    renderSource: null,
    steps: [],
});

// A client-only kernel: one python step whose source is a `run(event)` module,
// invoked by the harness via `entry`. Used only for signals with no backend
// producer (CLIENT_ONLY_TRIGGERS).
const signalKernel = (id, command, title, description, source) => ({
    id,
    command,
    title,
    description,
    family: null,
    tier: "alert",
    renderSource: "signal",
    steps: [{ kind: "python", id: "signal", entry: "run", code: source }],
});

// ─────────────────────────────────────────────────────────────────────────
// A. PROMPT / CONTENT SIGNALS (read the text the model was sent)
// ─────────────────────────────────────────────────────────────────────────

export const credentialMaterialTrigger = signalDef(
    "trig-credential-material",
    "credential-material",
    "Credential material in prompts or agent activity",
    "Detects cloud, model-provider, VCS, SaaS, and package-registry credentials; JWTs; auth headers; private keys; database connection strings; embedded URL credentials; and secret assignments in the newest turn, bounded tool results, and executable command fields. Plain mentions are ignored. Matched values are redacted.",
);

export const unicodeSmugglingTrigger = signalDef(
    "trig-unicode-smuggling",
    "unicode-smuggling",
    "Unicode smuggling characters",
    "Finds hidden text encoded in invisible characters and decodes it where possible. Invisible characters used in normal text do not fire this signal.",
);

// ─────────────────────────────────────────────────────────────────────────
// B. ACTION SIGNALS (read what the agent did: tool_calls)
// ─────────────────────────────────────────────────────────────────────────

export const reverseShellTrigger = signalDef(
    "trig-reverse-shell",
    "reverse-shell",
    "Reverse shell / C2 pattern",
    "Detects common reverse-shell and command-and-control patterns in tool-call arguments or the newest turn.",
);

export const encodedPayloadTrigger = signalDef(
    "trig-encoded-payload",
    "encoded-payload",
    "Encoded payload (base64 → URL/command)",
    "Detects base64 content in tool-call arguments or the newest turn that decodes to a URL or shell command. Requires valid UTF-8 and skips JSON and JWT content.",
);

// ─────────────────────────────────────────────────────────────────────────
// C. AUDIT / VISIBILITY SIGNALS (normal agent ops, ranked below alerts in the console)
// ─────────────────────────────────────────────────────────────────────────

export const destructiveCommandTrigger = signalDef(
    "trig-destructive-command",
    "destructive-command",
    "Destructive command",
    "Tags a trace where a potentially highly destructive command was executed. The tag value is the command that matched. This is an audit signal and does not identify a threat by itself.",
);

export const sensitivePathAccessTrigger = signalDef(
    "trig-sensitive-path-access",
    "sensitive-path-access",
    "Sensitive path accessed",
    "Records access to credential stores, naming which one: SSH private keys, cloud credentials, package and version-control tokens, the coding agent's own tokens, the system password file or keychain, browser login data, shell history, .env files, and keychain or secret-manager reads. Each finding carries the file, the operation, and whether the access succeeded. This is an audit signal; review it alongside egress findings.",
);

export const packageInstallTrigger = signalDef(
    "trig-package-install",
    "package-install",
    "Package install",
    "Records third-party package installation commands, with one finding per package manager.",
);

export const agentConfigModificationTrigger = signalDef(
    "trig-agent-config-modification",
    "agent-config-modification",
    "Agent configuration modified",
    "Detects an agent writing to a file that configures itself or another AI coding harness, and reports which setting changed.",
);

export const privilegeEscalationTrigger = signalDef(
    "trig-privilege-escalation",
    "privilege-escalation",
    "Privilege escalation",
    "Detects explicit root-shell launches, elevated Windows process launches, and setuid grants.",
);

export const EXAMPLE_TRIGGERS = [
    // A. Prompt / content
    credentialMaterialTrigger,
    unicodeSmugglingTrigger,
    // B. Actions (tool_calls)
    reverseShellTrigger,
    encodedPayloadTrigger,
    // C. Audit / visibility (normal agent ops, ranked below risk.* in the console)
    destructiveCommandTrigger,
    sensitivePathAccessTrigger,
    packageInstallTrigger,
    agentConfigModificationTrigger,
    privilegeEscalationTrigger,
];

// command → [backend signal_tags namespace, console tier]. The single
// authoritative wiring between an FE signal and its backend counterpart. The
// backend signals-catalog (O-2586) emits every signal under `security.<name>`,
// collapsing the old risk/pii/audit namespaces into one prefix; the dashboard
// reads signal_tags BY namespace, so `family` must equal that backend namespace.
// `tier` preserves the alert-vs-audit distinction the namespace no longer carries
// (audit = normal agent ops, recorded but ranked below alerts in the console).
// A command absent here keeps its kernel default (no backend namespace → not in
// the live catalog below).
//
// credscan emits its alert band only (`security.credscan`, the 0.858 feed) --
// the review-band remainder is detected but unemitted, and arrives as its own
// signal with the classifier work. Nothing extra to wire here until then.
const SIGNAL_BACKEND = {
    "credential-material":      ["security.credscan", "alert"],
    "unicode-smuggling":        ["security.unicode_smuggling", "alert"],
    "reverse-shell":            ["security.reverse_shell", "alert"],
    "encoded-payload":          ["security.encoded_payload", "alert"],
    "destructive-command":      ["security.destructive_command", "audit"],
    "sensitive-path-access":    ["security.sensitive_path_access", "audit"],
    "package-install":          ["security.package_install", "alert"],
    "agent-config-modification": ["security.agent_config_modification", "alert"],
    "privilege-escalation":     ["security.privilege_escalation", "alert"],
};
for (const t of EXAMPLE_TRIGGERS) {
    const [namespace, tier] = SIGNAL_BACKEND[t.command] || [null, "alert"];
    t.family = namespace;
    t.tier = tier;
}

// The signals surfaced in the dashboard, read from the backend signal pipeline
// (signal_tags). Every EXAMPLE_TRIGGERS signal has a backend home in the
// signals-catalog (its `family` namespace, wired in SIGNAL_BACKEND). A signal
// whose namespace isn't yet backend-live would be filtered out by the `family`
// guard.
export const CATALOG_SIGNALS = EXAMPLE_TRIGGERS.filter((t) => t.family);

// ── Reference signals (NOT seeded) ─────────────────────────────────────
//
// The contextual-lookup pattern (a per-event analytics_query — "first time this
// endpoint used this provider") — kept as authoring references for the
// scan-wide context-batching engine (lib/signals/queryPlanner), NOT seeded (no
// backend `run(event)` equivalent). The PLANNED
// form declares a spec whose substituted value varies in exactly one place per
// event so the planner can exact-dedupe + IN-merge a whole scan into a few
// fetches. The legacy HAXL reference has been retained by id/command for
// compatibility, but migrated to the same declared query boundary because
// isolated Python has no host bridge. Kernel-form (lookup) triggers — no
// backend `run(event)` equivalent. Exported for docs / manual paste, not seeded.
export const firstProviderUsePlannedTrigger = {
    id: "trig-first-provider-use-planned",
    command: "first-provider-use-planned",
    title: "First provider use (planned context)",
    description: "Reference: declared-prefetch demo of the context-batching planner. Fetches per-provider first-use timestamps for {{event.endpoint_id}}, shaped so only ONE value varies per event; a scan's worth exact-dedupes per endpoint and IN-merges across endpoints into a handful of fetches.",
    renderSource: "annotate",
    steps: [
        {
            kind: "analytics_query", id: "first_use", spec: {
                root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
                timeRange: { relative: { durationSeconds: String(90 * 86400) } },
                filter: {
                    pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, op: "OPERATOR_EQ", value: "{{event.endpoint_id}}" },
                },
                output: {
                    dimensions: [
                        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" }, outputAlias: "provider" },
                    ],
                    metrics: [{
                        outputAlias: "first_seen",
                        computed: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, op: "AGGREGATE_OP_MIN" },
                    }],
                },
            },
        },
        {
            kind: "python", id: "annotate", inputs: { fu: "first_use" }, code: `
event = params["event"]
rows = inputs.get("fu") or []
provider = event.get("provider") or ""
first_seen = None
for r in rows:
    if (r.get("provider") or "") == provider:
        first_seen = r.get("first_seen")
        break
result = None
if provider and event.get("endpoint_id"):
    is_first = first_seen is None or str(first_seen) >= str(event.get("event_time") or "")
    if is_first:
        result = {
            "subject_type": "endpoint",
            "subject_id": event.get("endpoint_id"),
            "type": "milestone.first_provider_use",
            "value": {"provider": provider, "first_seen": first_seen},
            "confidence": 0.9,
            "source": {"trigger_id": "trig-first-provider-use-planned"},
            "event_id": event.get("event_id"),
            "event_time": event.get("event_time"),
        }
`,
        },
    ],
};

export const firstProviderUseHaxlTrigger = {
    id: "trig-first-provider-use-haxl",
    command: "first-provider-use-haxl",
    title: "First provider use (declared migration)",
    description: "Reference: the former ctx.query demo migrated to an explicit analytics_query step. The scan planner batches the declared lookup, then passes JSON rows into isolated Python.",
    renderSource: "annotate",
    steps: [
        {
            kind: "analytics_query", id: "first_use", spec: {
                root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
                timeRange: { relative: { durationSeconds: String(90 * 86400) } },
                filter: {
                    pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, op: "OPERATOR_EQ", value: "{{event.endpoint_id}}" },
                },
                output: {
                    dimensions: [
                        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" }, outputAlias: "provider" },
                    ],
                    metrics: [{
                        outputAlias: "first_seen",
                        computed: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, op: "AGGREGATE_OP_MIN" },
                    }],
                },
            },
        },
        {
            kind: "python", id: "annotate", inputs: { fu: "first_use" }, code: `
event = params["event"]
rows = inputs.get("fu") or []
provider = event.get("provider") or ""
endpoint_id = event.get("endpoint_id") or ""
result = None
if provider and endpoint_id:
    first_seen = None
    for r in rows:
        if (r.get("provider") or "") == provider:
            first_seen = r.get("first_seen")
            break
    if first_seen is None or str(first_seen) >= str(event.get("event_time") or ""):
        result = {
            "subject_type": "endpoint",
            "subject_id": endpoint_id,
            "type": "milestone.first_provider_use",
            "value": {"provider": provider, "first_seen": first_seen},
            "confidence": 0.9,
            "source": {"trigger_id": "trig-first-provider-use-haxl"},
            "event_id": event.get("event_id"),
            "event_time": event.get("event_time"),
        }
`,
        },
    ],
};

export const REFERENCE_TRIGGERS = [
    firstProviderUsePlannedTrigger,
    firstProviderUseHaxlTrigger,
];

// ── Experimental cost-efficiency signal (client-only, NOT seeded) ──────
//
// A converged `run(event)` kernel - the SAME python contract the backend
// signals-catalog runs, so it's backend-portable the day a producer exists. NOT
// in EXAMPLE_TRIGGERS / CATALOG_SIGNALS (no backend `family`), so it never enters
// the seeded catalog or the customer signals listing. Run only by the
// client-only on-load trace scan in ContextPanel (useTraceSignalHighlights).
//
// Unlike a code-STYLE linter (which reads as after-the-fact nagging), this flags
// OBJECTIVE token/$ waste in how the agent used its tools — content it paid to
// put in context more than once, or a needlessly large read. Behavioral, not
// stylistic, and language-agnostic. Each finding anchors to the wasteful
// tool_use block (loc {surface:"block", turn, block, match}) so the trace detail
// marks the offending call. The kernel's python carries no backticks/backslashes
// of its own, so the JS template + python layers don't fight.
export const tokenEfficiencyTrigger = signalKernel(
    "trig-token-efficiency",
    "token-efficiency",
    "Token efficiency",
    "Experimental, client-only signal for avoidable token use. Detects repeated reads of unchanged files, reads immediately after a write, and unbounded reads of very large files. Reports one finding per tool call.",
    `
READ_TOOLS = {"read", "read_file", "readfile", "notebookread", "view"}
RANGE_KEYS = ("offset", "limit", "line_offset", "line_limit", "range", "view_range", "start_line", "end_line")
BIG_READ_BYTES = 100000  # ~25k tokens; a full read this large should have been ranged / grepped

def _file_path(inp):
    if not isinstance(inp, dict):
        return None
    for k in ("file_path", "path", "notebook_path", "filePath"):
        v = inp.get(k)
        if isinstance(v, str) and v:
            return v
    return None

# A tool call is classified by its INPUT, not its name (harness-agnostic):
def _writes_full(inp):
    # Write / create — authors the ENTIRE file content.
    return isinstance(inp.get("content"), str) or isinstance(inp.get("file_text"), str)

def _is_partial_edit(inp):
    # Edit / MultiEdit / str_replace — a hunk. Shifts line numbers and returns only
    # its own change, so it does NOT put the whole file in context.
    if isinstance(inp.get("edits"), list):
        return True
    for k in ("old_string", "new_string", "old_str", "new_str", "insert_text"):
        if isinstance(inp.get(k), str):
            return True
    return False

def _content_len(c):
    if isinstance(c, str):
        return len(c)
    if isinstance(c, list):
        n = 0
        for x in c:
            if isinstance(x, str):
                n += len(x)
            elif isinstance(x, dict) and isinstance(x.get("text"), str):
                n += len(x["text"])
        return n
    return 0

def _result_is_error(b):
    if b.get("isError") or b.get("is_error"):
        return True
    c = b.get("content")
    if isinstance(c, str):
        return "<tool_use_error>" in c
    if isinstance(c, list):
        for x in c:
            t = x if isinstance(x, str) else (x.get("text") if isinstance(x, dict) else "")
            if isinstance(t, str) and "<tool_use_error>" in t:
                return True
    return False

def run(event):
    turns = event.get("turns") if isinstance(event, dict) else None
    if not isinstance(turns, list):
        return []
    # Per tool_use id: result size (for the large-read check) and whether it
    # ERRORED. A failed call must not update state or fire — e.g. an Edit that
    # returns "File has not been read yet" did NOT write the file, so the read the
    # agent is then forced to do is required, not a read-after-write.
    result_len = {}
    errored = set()
    for turn in turns:
        blocks = turn.get("blocks") if isinstance(turn, dict) else None
        if not isinstance(blocks, list):
            continue
        for b in blocks:
            if isinstance(b, dict) and b.get("type") == "tool_result":
                tid = b.get("tool_use_id")
                if tid:
                    result_len[tid] = _content_len(b.get("content"))
                    if _result_is_error(b):
                        errored.add(tid)

    tags = []
    # have_full[path] = "read" | "write" when the file's ENTIRE CURRENT content is
    # in context: set by a full (un-ranged) Read or a full Write. A partial Edit
    # CLEARS it — the edit shifts line numbers and returns only its own hunk, so a
    # later read (even a ranged one) may fetch content the agent doesn't have. That
    # is why an edit-then-read is NOT flagged: the read's range needn't lie within
    # anything already seen.
    have_full = {}

    def emit(rid, ti, bi, match):
        tags.append({
            "subject_type": "session",
            "namespace": "efficiency",
            "tag": rid,
            "value": match if len(match) <= 160 else match[:160],
            "loc": {"surface": "block", "turn": ti, "block": bi, "match": match, "rule": rid},
        })

    for ti, turn in enumerate(turns):
        blocks = turn.get("blocks") if isinstance(turn, dict) else None
        if not isinstance(blocks, list):
            continue
        for bi, b in enumerate(blocks):
            if not isinstance(b, dict) or b.get("type") != "tool_use":
                continue
            if b.get("id") in errored:
                continue  # the call failed — no content read/written, so no waste
            inp = b.get("input") if isinstance(b.get("input"), dict) else {}
            path = _file_path(inp)
            if not path:
                continue
            name = (b.get("name") or "").strip().lower()
            if _writes_full(inp):
                have_full[path] = "write"       # whole file authored — content current in context
            elif _is_partial_edit(inp):
                have_full.pop(path, None)        # partial edit — prior full knowledge is now stale
            elif name in READ_TOOLS:
                src = have_full.get(path)
                is_full = not any(k in inp for k in RANGE_KEYS)
                if src == "write":
                    emit("read-after-write", ti, bi, path)
                elif src == "read":
                    emit("redundant-read", ti, bi, path)
                elif is_full and result_len.get(b.get("id"), 0) >= BIG_READ_BYTES:
                    emit("unbounded-large-read", ti, bi, path)
                if is_full:
                    have_full[path] = "read"     # full read — whole content now in context
    return tags
`,
);

// Client-only signals run by the on-load trace scan and kept out of the seeded
// server catalog because they are evaluated locally for rendered span offsets.
export const CLIENT_ONLY_TRIGGERS = [tokenEfficiencyTrigger];
